import React, { useEffect, useState } from "react";
import Menu from "@/components/menu";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItem from "@mui/material/ListItem";
import Header from "./header";
import * as styles from "./style.module.scss";
import {
  setNotificationsToState,
  selectNotifications,
  selectNotificationsUnreadCount,
  getPageInfo,
} from "@/store/slices/notifications/notificationsSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ToastTemplate } from "@/services/variables";
import Link from "next/link";
import NestedMessage from "./nested-message";
import NotificationLabel from "./notification-labels";
import NotificationHeading from "./notification-heading";
import NotificationText from "./notification-text";
import NotificationStatusTrigger from "./notification-status-trigger";
import ListSubheader from "@mui/material/ListSubheader";
import Lead_signalr from "@/modules/crm/leads/signalr-constants";
import useSignalRClient from "@/services/signal-R/signalr-hubs";
import useFrontdoorNotifications from "@/hooks/useFrontdoorNotifications";
import { getParsedJson, camelize } from "@/services/generic.methods";
import { useRouter } from "next/router";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import moment from "moment";
import UIButton from "@/components/ui-controls/button";
import { KeyboardDoubleArrowDown } from "@mui/icons-material";
import Box from "@mui/material/Box";
import TruncateTooltip from "@/components/truncate-with-tooltip";
import { GetFontAwesomeIcon } from "@/components/font-awesome-icon";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { notificationTypes } from "@/store/slices/notifications/types";
import {
  setImportMode,
  setSelectedImportToState,
} from "@/store/slices/import/importSlice";
import {
  LEAD_IMPORT_COMPLETED_TYPE,
  mapLeadImportNotificationToSeed,
} from "@/modules/crm/leads/components/import-flow/helpers";

const getNotificationAvatarStyles = (variant) => {
  switch (variant) {
    case "upcoming":
      return { background: "#FFF0E5", color: "#FF6B00" };
    case "appointment":
      return {
        background: "#EDE7F6",
        color: "var(--palette-secondary_button_text, #4f378b)",
      };
    default:
      return {
        background: "var(--palette-stroke)",
        color: "var(--palette-secondary_button_text)",
      };
  }
};

const APPOINTMENT_EVENT_TYPES = [
  "EventCreated",
  "EventConfirmation",
  "EventScheduled",
  "EventUpdated",
  "EventRescheduled",
  "EventReminder",
  "EventCancelled",
  "EventCanceled",
];

const buildAppointmentCalendarQuery = (data) => {
  const parsed = typeof data === "string" ? getParsedJson(data) : data || {};
  const query = {};
  const dateVal =
    parsed?.start_time || parsed?.startDate || parsed?.start || parsed?.date;
  if (dateVal) {
    query.date = moment(dateVal).format("YYYY-MM-DD");
  }
  if (parsed?.event_id) {
    query.eventId = String(parsed.event_id);
  }
  query.openAt = String(Date.now());
  return query;
};

const NotificationMenu = ({ anchorEl, isMenuOpen, handleMenuClose }) => {
  const router = useRouter();
  const { getNotifications, setNotificationIsRead, markAllAsRead } =
    useFrontdoorNotifications();
  const {
    hubName,
    InvokeMethodNames: { markNotificationAsRead },
  } = Lead_signalr;

  const { statuses, RTC } = useSignalRClient({ hubName });
  const leadhub = RTC[hubName];
  const leadhubStatus = statuses[hubName];
  const [loading, setLoading] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState("direct");
  const dateGroups = [
    "Today",
    "Yesterday",
    "Earlier this Week",
    "Last Week",
    "Other",
  ];
  const { notifications } = useSelector(selectNotifications);
  const unreadCount = useSelector(selectNotificationsUnreadCount);
  const pageInfo = useSelector(getPageInfo);
  const hasMoreNotifications = Boolean(pageInfo?.hasNextPage);

  const [filteredNotifications, setFilteredNotifications] = useState([]);

  const dispatch = useDispatch();
  const profile = useSelector(getLoggedinUser);

  const fetchNotifications = async () => {
    let payload = {
      where: {
        userEmail: {
          in: [profile?.Email, "workflow@systemuser.com"],
        },
      },
      order: [{ id: "DESC" }],
      skip: notifications?.length || pageInfo?.skip,
      take: pageInfo?.take || 10,
    };

    setLoading(true);
    try {
      const response = await getNotifications(payload);
      // .then((response) => {
      let payload2 = JSON.parse(JSON.stringify(response ?? []));
      payload2 = payload2.map((notification) => {
        return {
          ...notification,
          loggedInUserId: profile?.UserID,
        };
      });
      dispatch(setNotificationsToState(payload2));
      setLoading(false);
      // });
    } catch (error) {
      console.log("Error fetching notifications", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.Email && profile?.UserGUID) {
      fetchNotifications();
    }
  }, [profile]);

  const handlerFilterNotification = (allNotifications) => {
    const filtered = allNotifications.filter((notification) => {
      if (selectedOpt === "unread") {
        return !notification.isNotificationRead;
      } else if (selectedOpt === "following") {
        return notification.isFollowing;
      } else if (selectedOpt === "direct") {
        return true;
      }
    });
    return filtered;
  };

  // filter data on tab selection
  useEffect(() => {
    const filtered = notifications.filter((notification) => {
      if (selectedOpt === "unread") {
        return !notification.isNotificationRead;
      } else if (selectedOpt === "following") {
        return notification.isFollowing;
      } else if (selectedOpt === "direct") {
        return true;
      }
    });
    setFilteredNotifications(filtered);
  }, [selectedOpt, notifications]);

  const onChangeOption = (event) => {
    let selected = event.currentTarget.name;
    if (event.currentTarget.name === "unread") {
      selected = event.currentTarget.checked ? "unread" : "direct";
    }
    setSelectedOpt(selected);
  };

  // when mark read triggered
  const handleNotificationIsRead = (id) => {
    setNotificationIsRead(id)
      .then((res) => {
        console.log("Notification marked as read via Frontdoor:", res);
      })
      .catch((error) => {
        console.error("Error marking notification as read:", error);
      });
  };

  // mark all notifications as read
  const handleMarkAllAsRead = async () => {
    try {
      const result = await markAllAsRead();
      if (result) {
        toast.success(
          ToastTemplate.success("All notifications marked as read")
        );
      } else {
        toast.error(
          ToastTemplate.error("Failed to mark all notifications as read")
        );
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error(
        ToastTemplate.error("Failed to mark all notifications as read")
      );
    }
  };

  //unfollow a lead
  const handleUnFollowLead = (id) => {
    console.log("UnFollow Lead", id);
  };
  const redirectNotification = (e, notification) => {
    e.preventDefault();
    let route = "/crm/leads";
    handleNotificationIsRead(notification?.notificationId || notification?.id);

    if (notification?.type === LEAD_IMPORT_COMPLETED_TYPE) {
      const seed = mapLeadImportNotificationToSeed(notification);
      if (seed?.id) {
        dispatch(setSelectedImportToState(seed));
        dispatch(setImportMode(2));
        router.push("/crm/leads/import");
      } else {
        router.push("/crm/leads/import");
      }
      handleMenuClose();
      return;
    }

    if (APPOINTMENT_EVENT_TYPES.includes(notification?.type)) {
      router.push({
        pathname: "/crm/appointments/calendars",
        query: buildAppointmentCalendarQuery(notification?.data),
      });
    } else if (
      notification?.type === "LeadExport" ||
      notification?.type === "ReportExport"
    ) {
      // Export files are downloaded once when notification arrives.
      // Clicking the notification should only mark it read/open notification center.
    }

    if (
      Object.keys(notificationTypes.task).includes(camelize(notification?.type))
    ) {
      route = `/crm/sales/tasks/view/${notification?.data?.id}`;
      router.push(route);
    } else if (notification?.leadId) {
      route = {
        pathname: `${route}/view/[id]`,
        query: { id: notification.leadId },
      };
      let query = notification?.type?.includes("Email")
        ? "email"
        : "conversation";
      if (notification?.data?.conversationId) {
        route.query[query] = notification?.data?.conversationId;
      }
      router.push(route);
    } else if (
      notification?.type === "WorkflowRun" &&
      router.pathname !== "/crm/automation/automations"
    ) {
      router.push("/crm/automation/automations");
    }

    handleMenuClose();
  };

  const menuId = "primary-search-notification-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id={menuId}
      open={isMenuOpen}
      onClose={handleMenuClose}
      classes={{ paper: styles.notificationMenu }}
      style={{ border: "none" }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <Header
        selected={selectedOpt}
        onChange={onChangeOption}
        onMarkAllAsRead={handleMarkAllAsRead}
        unreadCount={unreadCount}
      />
      <List className={`${styles?.listContainer} custom-scroll`}>
        {dateGroups.map(
          (group) =>
            filteredNotifications?.filter((x) => x.dateGroup === group).length >
              0 && (
              <List
                key={group}
                subheader={
                  <ListSubheader
                    style={{
                      color: "var(--palette-sub_heading)",
                      borderBottom: "1px solid var(--palette-stroke)",
                      backgroundColor: "var(--palette-container)",
                    }}
                  >
                    {group}
                  </ListSubheader>
                }
                style={{ paddingBottom: 0 }}
              >
                {filteredNotifications
                  ?.filter((x) => x.dateGroup === group)
                  .map((item, index) => {
                    const avatarStyles = getNotificationAvatarStyles(
                      item?.variant
                    );
                    return (
                      <ListItem
                        className={styles?.listItemButton}
                        key={index}
                        alignItems="flex-start"
                        divider
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                          className={`${styles.statusTrigger}`}
                        >
                          {item?.isFollowing && (
                            <NotificationStatusTrigger
                              isFollowing={true}
                              title={"Unfollow a lead"}
                              notification={item}
                              handleClick={handleUnFollowLead}
                            />
                          )}
                          {!item?.isNotificationRead && (
                            <NotificationStatusTrigger
                              isFollowing={false}
                              title={"Mark as read"}
                              notification={item}
                              handleClick={handleNotificationIsRead}
                            />
                          )}
                        </span>
                        <Link
                          className={styles.notificationLink}
                          href=""
                          onClick={(e) => redirectNotification(e, item)}
                        >
                          <ListItemAvatar>
                            <Box
                              sx={{
                                borderRadius: "50%",
                                height: "40px",
                                width: "40px",
                                background: avatarStyles.background,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <GetFontAwesomeIcon
                                icon={item?.icon || faEnvelope}
                                iconStyles={{
                                  marginRight: "0px",
                                  fontSize: "1.4rem",
                                  color: avatarStyles.color,
                                }}
                              />
                            </Box>
                            {/* <NotificationAvatar notification={item} /> */}
                          </ListItemAvatar>
                          <div className={styles.nestedNotificationItems}>
                            <ListItemText
                              primary={
                                <NotificationHeading
                                  title={
                                    <TruncateTooltip
                                      text={item?.title}
                                      limit={32}
                                    />
                                  }
                                  text={moment
                                    .utc(item?.createdOn)
                                    .local()
                                    .fromNow()}
                                />
                              }
                              secondary={<NotificationText text={item?.text} />}
                            />

                            {item?.message && (
                              <NestedMessage notification={item} />
                            )}

                            {!!item?.labels?.length &&
                              item?.labels?.map((label, index) => {
                                return (
                                  <NotificationLabel
                                    label={label}
                                    key={index}
                                  />
                                );
                              })}
                          </div>
                        </Link>
                      </ListItem>
                    );
                  })}
              </List>
            )
        )}
      </List>
      {hasMoreNotifications && notifications?.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 1.5,
            px: 2,
            flexShrink: 0,
            borderTop: "1px solid var(--palette-stroke)",
            backgroundColor: "var(--palette-container)",
          }}
        >
          <UIButton
            variant="outlined"
            loading={loading}
            disabled={loading}
            onClick={() => {
              fetchNotifications();
            }}
            sx={{
              borderRadius: "8px",
              borderColor: "var(--palette-secondary_button_stroke, #d0cdeb)",
              color: "var(--palette-secondary_button_text, #4f378b)",
              "&:hover": {
                borderColor: "var(--palette-secondary_button_stroke)",
                backgroundColor: "rgba(79, 55, 139, 0.04)",
              },
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
            }}
          >
            Load More&nbsp;
            <KeyboardDoubleArrowDown fontSize="small" sx={{ ml: 0.5 }} />
          </UIButton>
        </Box>
      )}
    </Menu>
  );
  return renderMenu;
};

export default NotificationMenu;
